import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../tokens";
import { Badge } from "../components/Badge";

// ── ExH1B Partner Review ────────────────────────────────────────────────────
// Read-only surface for ExH1B reviewers. Calls the scoped backend endpoint,
// which returns ONLY ExH1B-sourced candidates with a safe projection (no
// provider/mapping ids, tokens, urls, or internal fields). Direct and other
// Caliber users are never returned. Access is enforced server-side (403 for
// non-partner accounts).

type PartnerUser = {
  candidateId: string;
  name: string;
  email: string;
  label: string;   // "ExH1B Candidate"
  source: string;  // "ExH1B"
  joinedAt: string;
  isActive: boolean;
  matchCount: number;
};

type PartnerUsersPage = { items: PartnerUser[]; total: number; page: number; limit: number };

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().slice(0, 10);
}

export function PartnerUsers() {
  const q = useQuery<PartnerUsersPage>({
    queryKey: ["partner-users"],
    queryFn: async () => {
      const { data } = await axios.get("/api/admin/partner/users", { params: { limit: 100 } });
      return data;
    },
    retry: false,
  });

  const forbidden = axios.isAxiosError(q.error) && q.error.response?.status === 403;

  return (
    <div style={{ minHeight: "100vh", background: T.s1, padding: "28px 20px" }}>
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <h1 style={{ fontFamily: T.body, fontSize: 20, color: T.t0, margin: 0 }}>ExH1B Partner Review</h1>
          <Badge variant="teal" size="sm">ExH1B</Badge>
        </div>
        <p style={{ fontFamily: T.mono, fontSize: 11, color: T.t3, marginTop: 0, marginBottom: 18 }}>
          Read-only · ExH1B-sourced candidates only
        </p>

        {q.isLoading && (
          <div style={{ fontFamily: T.mono, fontSize: 12, color: T.t2 }}>Loading…</div>
        )}

        {forbidden && (
          <div style={{ fontFamily: T.body, fontSize: 13, color: T.warn, background: T.warnBg,
            border: `1px solid ${T.warnBdr}`, borderRadius: 8, padding: "12px 14px" }}>
            You don’t have ExH1B Partner access on this account.
          </div>
        )}

        {q.isError && !forbidden && (
          <div style={{ fontFamily: T.body, fontSize: 13, color: T.err }}>Failed to load candidates.</div>
        )}

        {q.data && (
          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1.6fr 0.8fr 0.7fr 0.6fr",
              gap: 12, padding: "9px 16px", borderBottom: `1px solid ${T.b0}`,
              fontFamily: T.mono, fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", color: T.t3 }}>
              <span>Candidate</span><span>Email</span><span>Joined</span><span style={{ textAlign: "center" }}>Matches</span><span style={{ textAlign: "center" }}>Active</span>
            </div>

            {q.data.items.length === 0 && (
              <div style={{ padding: "16px", fontFamily: T.mono, fontSize: 12, color: T.t3 }}>
                No ExH1B candidates yet.
              </div>
            )}

            {q.data.items.map((u) => (
              <div key={u.candidateId} style={{ display: "grid",
                gridTemplateColumns: "1.4fr 1.6fr 0.8fr 0.7fr 0.6fr", gap: 12, padding: "10px 16px",
                borderBottom: `1px solid ${T.b0}`, alignItems: "center" }}>
                <span style={{ fontFamily: T.body, fontSize: 13, color: T.t0, display: "flex",
                  alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</span>
                  <Badge variant="teal" size="sm" style={{ flexShrink: 0 }}>{u.label}</Badge>
                </span>
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.t2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</span>
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.t3 }}>{fmtDate(u.joinedAt)}</span>
                <span style={{ fontFamily: T.mono, fontSize: 12, textAlign: "center",
                  color: u.matchCount > 0 ? T.tealSoft : T.t3 }}>{u.matchCount}</span>
                <span style={{ textAlign: "center", fontSize: 11, color: u.isActive ? T.ok : T.t3 }}>
                  {u.isActive ? "✓" : "–"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
